import { getProviderTimezone } from './timezone-utils';

/**
 * Converts a date or time to UTC based on the provider's timezone
 * @param {string} input - The date (MM-DD-YYYY), time (HH:mm or HH:mm:ss AM/PM), or ISO date string to convert
 * @returns {string} The UTC date (MM-DD-YYYY), time (HH:mm), or ISO string
 */
export const convertToUTC = (input) => {
  try {
    const providerTimezone = getProviderTimezone();
    
    // Check if input is an ISO date string (YYYY-MM-DDTHH:mm:ss.sssZ or similar)
    if (typeof input === 'string' && (input.includes('T') || input.includes('Z') || /^\d{4}-\d{2}-\d{2}/.test(input))) {
      
      // Parse the ISO date string
      const isoDate = new Date(input);
      
      if (isNaN(isoDate.getTime())) {
        throw new Error('Invalid ISO date format');
      }
      
      // Return ISO string in UTC (already in UTC format)
      const utcISOString = isoDate.toISOString();
      
      return utcISOString;
    }
    
    // Check if input is an ISO date string (YYYY-MM-DDTHH:mm:ss.sssZ or similar)
    if (typeof input === 'string' && (input.includes('T') || input.includes('Z') || /^\d{4}-\d{2}-\d{2}/.test(input))) {
      console.log('Debug - Input ISO Date:', input);
      
      // Parse the ISO date string
      const isoDate = new Date(input);
      
      if (isNaN(isoDate.getTime())) {
        throw new Error('Invalid ISO date format');
      }
      
      // Return ISO string in UTC (already in UTC format)
      const utcISOString = isoDate.toISOString();
      console.log('Debug - UTC ISO String:', utcISOString);
      
      return utcISOString;
    }
    
    // Check if input is a time (HH:mm or HH:mm:ss AM/PM)
    if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?\s?(?:AM|PM|am|pm)?$/.test(input)) {
      // Handle time conversion
      let hours, minutes, seconds = '00';
      
      // Check if it's 12-hour format
      if (input.toLowerCase().includes('am') || input.toLowerCase().includes('pm')) {
        const [time, period] = input.split(' ');
        const timeParts = time.split(':');
        [hours, minutes] = timeParts.map(Number);
        if (timeParts.length === 3) {
          seconds = timeParts[2];
        }
        
        // Convert to 24-hour format
        if (period.toLowerCase() === 'pm' && hours !== 12) {
          hours += 12;
        } else if (period.toLowerCase() === 'am' && hours === 12) {
          hours = 0;
        }
      } else {
        const timeParts = input.split(':');
        [hours, minutes] = timeParts.map(Number);
        if (timeParts.length === 3) {
          seconds = timeParts[2];
        }
      }

      const today = new Date();
      today.setHours(hours, minutes, seconds, 0);
      
      const utcDate = new Date(today.toLocaleString('en-US', { timeZone: providerTimezone }));
      return `${String(utcDate.getUTCHours()).padStart(2, '0')}:${String(utcDate.getUTCMinutes()).padStart(2, '0')}:${String(utcDate.getUTCSeconds()).padStart(2, '0')}`;
    }
    
    // Check if input is a date (MM-DD-YYYY)
    if (/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/.test(input)) {
      
      // Parse the input date
      const [month, day, year] = input.split('-').map(Number);
      
      // Create a date object at midnight in the local timezone
      const localDate = new Date(year, month - 1, day, 0, 0, 0);
      
      // Convert to UTC
      const utcDate = new Date(localDate.getTime() - (localDate.getTimezoneOffset() * 60000));
      
      // Format as MM-DD-YYYY
      const utcMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const utcDay = String(utcDate.getUTCDate()).padStart(2, '0');
      const utcYear = utcDate.getUTCFullYear();
      
      const formattedUTCDate = `${utcMonth}-${utcDay}-${utcYear}`;
      
      return formattedUTCDate;
    }
    
    throw new Error('Invalid input format. Expected MM-DD-YYYY, HH:mm, HH:mm:ss AM/PM, or ISO date string');
  } catch (error) {
    console.error('Error converting to UTC:', error);
    throw error;
  }
};

/**
 * Converts a UTC time back to the provider's timezone
 * @param {string} utcTime - The UTC time in 24-hour format (HH:mm)
 * @returns {string} The time in the provider's timezone (HH:mm)
 */
export const convertTimeFromUTC = (utcTime) => {
  try {
    const providerTimezone = getProviderTimezone();
    
    // Parse the UTC time
    const [hours, minutes] = utcTime.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
      throw new Error('Invalid UTC time format');
    }

    // Create a date object for today with the UTC time
    const today = new Date();
    today.setUTCHours(hours, minutes, 0, 0);

    // Convert to provider's timezone
    const localTime = today.toLocaleTimeString('en-US', {
      timeZone: providerTimezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    return localTime;
  } catch (error) {
    console.error('Error converting time from UTC:', error);
    throw new Error('Invalid UTC time format');
  }
};

/**
 * Converts a UTC date back to the provider's timezone
 * @param {string} utcDate - The UTC date in ISO format (YYYY-MM-DD)
 * @returns {string} The date in DD-MM-YYYY format
 */
export const convertDateFromUTC = (utcDate) => {
  try {
    const providerTimezone = getProviderTimezone();
    const date = new Date(utcDate);
    
    if (date.toString() === 'Invalid Date') {
      throw new Error('Invalid UTC date format');
    }

    // Convert to provider's timezone
    const localDate = date.toLocaleDateString('en-US', {
      timeZone: providerTimezone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Convert from MM/DD/YYYY to DD-MM-YYYY
    const [month, day, year] = localDate.split('/');
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error converting date from UTC:', error);
    throw new Error('Invalid UTC date format');
  }
};

/**
 * Validates if a string is a valid time
 * @param {string} time - The time string to validate (format: "HH:mm" or "HH:mm AM/PM")
 * @returns {boolean} Whether the time is valid
 */
export const isValidTime = (time) => {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](?:\s?[AaPp][Mm])?$/;
  return timeRegex.test(time);
};

/**
 * Validates if a string is a valid date
 * @param {string} date - The date string to validate (format: "DD-MM-YYYY")
 * @returns {boolean} Whether the date is valid
 */
export const isValidDate = (date) => {
  const dateRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;
  if (!dateRegex.test(date)) return false;

  const [day, month, year] = date.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day);
  return dateObj.getDate() === day && 
         dateObj.getMonth() === month - 1 && 
         dateObj.getFullYear() === year;
};

/**
 * Formats a date/time string to a specific format
 * @param {string|Date} input - The date/time to format
 * @param {string} format - The desired format (e.g., 'MM/DD/YYYY', 'HH:mm')
 * @returns {string} The formatted date/time string
 */
export const formatDateTime = (input, format) => {
  try {
    const date = input instanceof Date ? input : new Date(input);
    const providerTimezone = getProviderTimezone();
    
    const options = {
      timeZone: providerTimezone,
      year: format.includes('YYYY') ? 'numeric' : undefined,
      month: format.includes('MM') ? '2-digit' : undefined,
      day: format.includes('DD') ? '2-digit' : undefined,
      hour: format.includes('HH') ? '2-digit' : undefined,
      minute: format.includes('mm') ? '2-digit' : undefined,
      hour12: format.includes('hh') || format.includes('a')
    };

    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting date/time:', error);
    throw new Error('Invalid date/time format');
  }
};

/**
 * Validates if a string is a valid date or time
 * @param {string} input - The date/time string to validate
 * @param {boolean} isTimeOnly - Whether the input should be time-only
 * @returns {boolean} Whether the input is valid
 */
export const isValidDateTime = (input, isTimeOnly = false) => {
  try {
    if (isTimeOnly) {
      // Validate time format (HH:mm or HH:mm AM/PM)
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](?:\s?[AaPp][Mm])?$/;
      return timeRegex.test(input);
    } else {
      // Validate date format
      const date = new Date(input);
      return date instanceof Date && !isNaN(date);
    }
  } catch (error) {
    return false;
  }
};

/**
 * Converts a UTC datetime string to local timezone and formats it
 * @param {string} utcDateTimeString - The UTC datetime string (ISO format)
 * @returns {string} Formatted datetime in provider's timezone (e.g., "Jan 15, 2025 | 03:45 PM")
 */
export const formatUTCToLocalDateTime = (utcDateTimeString) => {
  if (!utcDateTimeString) return '';
  
  try {
    const providerTimezone = getProviderTimezone();
    
    // Ensure the string is treated as UTC by appending 'Z' if not present
    let utcString = utcDateTimeString;
    if (!utcString.endsWith('Z') && !utcString.includes('+') && !utcString.includes('-', 10)) {
      utcString = utcString + 'Z';
    }
    
    const date = new Date(utcString);
    
    if (isNaN(date.getTime())) {
      console.error('❌ Invalid date string:', utcDateTimeString);
      return utcDateTimeString;
    }
    
    // Format the date part in the provider's timezone
    const datePart = date.toLocaleDateString('en-US', {
      timeZone: providerTimezone,
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
    
    // Format the time part in the provider's timezone
    const timePart = date.toLocaleTimeString('en-US', {
      timeZone: providerTimezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    // Combine: "Jan 15, 2025 | 03:45 PM"
    return `${datePart} | ${timePart}`;
  } catch (error) {
    console.error('❌ Error formatting UTC datetime to local:', error);
    return utcDateTimeString;
  }
}; 